import { RefreshCw, Check } from 'lucide-react';
import { useLanguage } from '../../../../features/language/useLanguage';

interface NotificationSaveBarProps {
  dirty: boolean;
  saving: boolean;
  onReset: () => void;
  onSave: () => void;
}

/** Sticky footer showing dirty state with reset/save actions. */
export function NotificationSaveBar({ dirty, saving, onReset, onSave }: NotificationSaveBarProps) {
  const { t } = useLanguage();
  return (
    <div className="mt-5 flex flex-wrap items-center justify-end gap-3 border-t border-gray-100 bg-gray-50/60 px-4 sm:px-6 py-4">
      {dirty ? (
        <span className="text-xs text-amber-700 inline-flex items-center gap-1.5 mr-auto">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
          {t('notifications.settings.unsavedChanges', undefined, 'Unsaved changes')}
        </span>
      ) : (
        <span className="text-xs text-gray-400 inline-flex items-center gap-1.5 mr-auto">
          <Check className="h-3.5 w-3.5 text-gray-400" />
          {t('notifications.settings.upToDate', undefined, 'All changes saved')}
        </span>
      )}
      {dirty && (
        <button
          type="button"
          onClick={onReset}
          disabled={saving}
          className="px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {t('common.cancel', undefined, 'Cancel')}
        </button>
      )}
      <button
        type="button"
        onClick={onSave}
        disabled={saving || !dirty}
        data-test-id="notification-save-button"
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
      >
        {saving ? (
          <>
            <RefreshCw className="h-4 w-4 animate-spin" />
            {t('common.saving', undefined, 'Saving…')}
          </>
        ) : (
          t('common.save', undefined, 'Save')
        )}
      </button>
    </div>
  );
}
